// The spoken agent's per-device switches (Settings → Voice). Not synced: a
// laptop's microphone and a phone's are different things. They are kept under
// localStorage `desk`, where the desk kept them, so a device's choices
// survive; other old desk keys in that value are left as they are (the
// outside widgets read the desk's place and entities from it, os/widgetConfig.ts).
import { create } from 'zustand'

export interface VoicePrefs {
  /** show words as they are said, from the browser's own recogniser running beside the agent */
  liveWords: boolean
  /** the mic is shut between turns; hold the centre button to talk. Off by default: the open mic is the point of a voice agent. */
  pushToTalk: boolean
}

/**
 * Live words default to on everywhere but a Mac: there the browser's
 * recogniser and the agent both hold the microphone and Chrome silences the
 * agent's audio. An iPad in desktop mode reports itself as a Mac, so touch
 * tells them apart.
 */
export const liveWordsDefault = () =>
  !(typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) && navigator.maxTouchPoints < 2)

const KEY = 'desk'

const stored = (): Record<string, unknown> => {
  try {
    const raw = localStorage.getItem(KEY)
    const j = raw ? (JSON.parse(raw) as unknown) : null
    return j && typeof j === 'object' && !Array.isArray(j) ? (j as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

const read = (): VoicePrefs => {
  const j = stored()
  return {
    liveWords: typeof j.liveWords === 'boolean' ? j.liveWords : liveWordsDefault(),
    pushToTalk: typeof j.pushToTalk === 'boolean' ? j.pushToTalk : false,
  }
}

export const useVoicePrefs = create<VoicePrefs & { set: (patch: Partial<VoicePrefs>) => void }>((set) => ({
  ...read(),
  set: (patch) => {
    set(patch)
    try {
      localStorage.setItem(KEY, JSON.stringify({ ...stored(), ...patch }))
    } catch {
      /* private mode: the switch holds for the session */
    }
  },
}))
